import React, { useState, useEffect } from 'react';
import { LOCApplication } from '../data/mockData';
import { formatCurrency, colors } from '../theme';
import StatusBadge from '../components/StatusBadge';
import api from '../services/api';

const Emergency: React.FC = () => {
  const [emergencyLOCs, setEmergencyLOCs] = useState<LOCApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const fetchEmergencies = () => {
    setLoading(true);
    api.emergency.getAll()
      .then(res => {
        const data = res.data || res;
        if (Array.isArray(data)) setEmergencyLOCs(data);
      })
      .catch((e) => setError(e.message || 'Failed to load data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEmergencies();
  }, []);

  const handleRatify = async (id: string) => {
    setActionLoading(id);
    try {
      await api.approvals.approve(id, 'Emergency ratified');
      fetchEmergencies();
    } catch (err: any) {
      alert('Ratification failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleTriggerVerification = async (id: string) => {
    setActionLoading(id);
    try {
      await api.loc.getById(id);
      alert('Hospital verification triggered for LOC ' + id);
    } catch (err: any) {
      alert('Verification trigger failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>🚨 Emergency LOCs</h2>
            <p>Provisional LOCs requiring ratification</p>
          </div>
        </div>
      </div>

      <div className="content">
        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>}
        {error && <div style={{ padding: '12px 20px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', borderRadius: 8, margin: '0 0 16px', fontSize: 13, fontWeight: 600 }}>{error}</div>}
        {/* Stats */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="stat-card" style={{ borderLeft: `3px solid ${colors.error}` }}>
            <h3>{emergencyLOCs.length}</h3>
            <p>Total Emergency LOCs</p>
          </div>
          <div className="stat-card" style={{ borderLeft: `3px solid ${colors.warning}` }}>
            <h3>{emergencyLOCs.filter(l => !['completed', 'rejected'].includes(l.status)).length}</h3>
            <p>Awaiting Ratification</p>
          </div>
          <div className="stat-card" style={{ borderLeft: `3px solid ${colors.success}` }}>
            <h3>{formatCurrency(emergencyLOCs.reduce((s, l) => s + l.estimatedAmount, 0))}</h3>
            <p>Total Emergency Amount</p>
          </div>
        </div>

        {emergencyLOCs.map(loc => (
          <div className="card" key={loc.id} style={{ borderLeft: `3px solid ${colors.error}` }}>
            <div className="card-body padded">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className="badge emergency"><span className="badge-dot" />EMERGENCY SOS</span>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>{loc.refNumber}</span>
                    <StatusBadge status={loc.status} />
                  </div>
                  <div style={{ fontSize: 13, color: colors.textSecondary }}>
                    Issued: {new Date(loc.submittedAt).toLocaleString('en-IN')} · Ratification deadline: 2 hours
                  </div>
                </div>
                <div className="amount" style={{ fontSize: 22 }}>{formatCurrency(loc.estimatedAmount)}</div>
              </div>

              <div className="grid-3">
                <div>
                  <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Officer</div>
                  <div style={{ fontWeight: 600 }}>{loc.officerName}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>{loc.category} · {loc.officerCfmsId}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Patient</div>
                  <div style={{ fontWeight: 600 }}>{loc.beneficiaryName} ({loc.relation})</div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>{loc.diagnosis}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Hospital</div>
                  <div style={{ fontWeight: 600 }}>{loc.hospitalName}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>{loc.hospitalCity} · {loc.treatment}</div>
                </div>
              </div>

              <div style={{ marginTop: 16, padding: 12, background: colors.warningSoft, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.warning }}>
                  ⚠ Hospital verification pending — back-fill required after emergency
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="header-btn primary" disabled={actionLoading === loc.id} onClick={() => handleRatify(loc.id)}>
                    {actionLoading === loc.id ? 'Processing...' : '✓ Ratify LOC'}
                  </button>
                  <button className="action-btn" disabled={actionLoading === loc.id} onClick={() => handleTriggerVerification(loc.id)}>
                    Trigger Verification
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {emergencyLOCs.length === 0 && (
          <div className="card">
            <div className="card-body">
              <div className="empty-state">
                <div className="empty-state-icon">✓</div>
                <h4>No Active Emergencies</h4>
                <p>No emergency LOCs requiring attention</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Emergency;
